import type {
  AdminSystemUser,
  AdminUserTenantInfo,
  UserTenantAssignment,
} from '../../../../domain/src/lib/admin-users/admin-user.model';
import {
  applyVaultOperativeRoleToSession,
  isSimappeProductCompanyLabel,
  isSimappeTechnicalRoleToken,
  normalizeVaultTenantId,
  persistVaultTenantScopeToStorage,
  persistVaultUserSwitchedTenant,
  readVaultOperativeRole,
  resolveVaultActiveTenantId,
  sessionJwtIndicatesVaultSuperadmin,
  vaultCurrentUserIdFromSession,
} from '@nebula/shared-vault/data-access';

function sanitizeVaultOperativeRole(raw: unknown): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed || isSimappeTechnicalRoleToken(trimmed)) return '';
  return trimmed;
}

function vaultRoleFromRecord(record: Record<string, unknown>): string {
  for (const key of [
    'vault_role',
    'vaultRole',
    'operative_role',
    'operativeRole',
    'tenant_role',
    'tenantRole',
    'role',
  ] as const) {
    const sanitized = sanitizeVaultOperativeRole(record[key]);
    if (sanitized) return sanitized;
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Desenvuelve listados API (`[]`, `{ data }`, `{ items }`, `{ content }`, `{ results }`). */
export function extractAdminUsersArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const root = asRecord(raw);
  if (!root) return [];
  for (const key of ['items', 'data', 'content', 'results', 'users'] as const) {
    const value = root[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function nestedTenantRecord(row: Record<string, unknown>): Record<string, unknown> | null {
  const nested = row['tenant'] ?? row['Tenant'];
  return asRecord(nested);
}

export function normalizeAdminUserTenantRow(raw: unknown): AdminUserTenantInfo | null {
  const tr = asRecord(raw);
  if (!tr) return null;

  const nested = nestedTenantRecord(tr);
  const role = sanitizeVaultOperativeRole(tr['role']);
  let tenant_id = String(
    tr['tenant_id'] ??
      tr['tenantId'] ??
      nested?.['id'] ??
      nested?.['tenant_id'] ??
      nested?.['tenantId'] ??
      '',
  ).trim();
  /** Legacy listado: `{ id, name, role }` sin `tenant_id` explícito. */
  if (!tenant_id && role) {
    const legacyId = String(tr['id'] ?? tr['user_tenant_id'] ?? tr['userTenantId'] ?? '').trim();
    if (legacyId) tenant_id = legacyId;
  }
  const tenant_name = String(
    tr['tenant_name'] ??
      tr['tenantName'] ??
      tr['name'] ??
      nested?.['name'] ??
      nested?.['tenant_name'] ??
      nested?.['tenantName'] ??
      '',
  ).trim();

  if (!tenant_id && !role) return null;

  return {
    id: String(tr['id'] ?? tr['user_tenant_id'] ?? tr['userTenantId'] ?? ''),
    tenant_id,
    role,
    tenant_name,
    tenant_slug:
      tr['tenant_slug'] != null
        ? String(tr['tenant_slug'])
        : tr['tenantSlug'] != null
          ? String(tr['tenantSlug'])
          : nested?.['slug'] != null
            ? String(nested['slug'])
            : undefined,
  };
}

export function normalizeAdminUserTenantsFromRecord(
  record: Record<string, unknown>,
): AdminUserTenantInfo[] {
  const raw = record['tenants'] ?? record['user_tenants'] ?? record['userTenants'];
  const rows: AdminUserTenantInfo[] = [];

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const row = normalizeAdminUserTenantRow(entry);
      if (row && (row.tenant_id || row.role)) rows.push(row);
    }
  }

  if (rows.length === 0) {
    const tenant_id = String(record['tenant_id'] ?? record['tenantId'] ?? '').trim();
    const role = vaultRoleFromRecord(record);
    if (tenant_id || role) {
      rows.push({
        id: '',
        tenant_id,
        role,
        tenant_name: String(record['tenant_name'] ?? record['tenantName'] ?? ''),
      });
    }
  }

  return dedupeAdminUserTenants(rows).map((row) => ({
    ...row,
    role: sanitizeVaultOperativeRole(row.role) || row.role,
  }));
}

export function dedupeAdminUserTenants(
  rows: AdminUserTenantInfo[],
): AdminUserTenantInfo[] {
  const byTenant = new Map<string, AdminUserTenantInfo>();
  for (const row of rows) {
    const key = (row.tenant_id || row.id || '').trim();
    if (!key) continue;
    byTenant.set(key, { ...byTenant.get(key), ...row, tenant_id: row.tenant_id || key });
  }
  return [...byTenant.values()];
}

const VAULT_ADMIN_USER_PRIMARY_TENANT_KEY = 'vault_admin_user_primary_tenant';

export function readAdminUserPrimaryTenantMap(): Record<string, string> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(VAULT_ADMIN_USER_PRIMARY_TENANT_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

export function readAdminUserPrimaryTenant(userId: string): string {
  const id = (userId || '').trim();
  if (!id) return '';
  return (readAdminUserPrimaryTenantMap()[id] || '').trim();
}

/** Guarda la empresa canonical asignada por admin (prioridad sobre duplicados legacy del API). */
export function persistAdminUserPrimaryTenant(userId: string, tenantId: string): void {
  const uid = (userId || '').trim();
  const tid = normalizeVaultTenantId(tenantId) || (tenantId || '').trim();
  if (!uid || !tid || typeof localStorage === 'undefined') return;
  const map = readAdminUserPrimaryTenantMap();
  map[uid] = tid;
  localStorage.setItem(VAULT_ADMIN_USER_PRIMARY_TENANT_KEY, JSON.stringify(map));
}

const VAULT_ADMIN_USER_ASSIGNMENT_SNAPSHOT_KEY = 'vault_admin_user_assignment_v1';

export interface AdminUserAssignmentSnapshot {
  tenant_id: string;
  role: string;
  tenant_name?: string;
}

/** Prioriza rol confirmado en Gestión de Usuarios sobre listados API eventualmente inconsistentes. */
function resolveAdminUserSnapshotRoleMerge(
  prev: AdminUserAssignmentSnapshot | undefined,
  apiRole: string,
  mergedTenantId: string,
): string {
  const tenant = mergedTenantId.trim();
  const prevRole = (prev?.role || '').trim();
  const prevTenant = (prev?.tenant_id || '').trim();
  if (prevTenant && prevTenant === tenant && prevRole) {
    return prevRole;
  }
  return (apiRole || '').trim() || prevRole || 'consulta';
}

function readAdminUserAssignmentSnapshotMap(): Record<string, AdminUserAssignmentSnapshot> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(VAULT_ADMIN_USER_ASSIGNMENT_SNAPSHOT_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, AdminUserAssignmentSnapshot>;
  } catch {
    return {};
  }
}

function writeAdminUserAssignmentSnapshotMap(
  map: Record<string, AdminUserAssignmentSnapshot>,
): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(VAULT_ADMIN_USER_ASSIGNMENT_SNAPSHOT_KEY, JSON.stringify(map));
}

export function readAdminUserAssignmentSnapshot(userId: string): AdminUserAssignmentSnapshot | null {
  const id = (userId || '').trim();
  if (!id) return null;
  const snapshot = readAdminUserAssignmentSnapshotMap()[id];
  if (snapshot?.tenant_id?.trim()) {
    return {
      tenant_id: snapshot.tenant_id.trim(),
      role: (snapshot.role || 'consulta').trim(),
      tenant_name: snapshot.tenant_name?.trim(),
    };
  }
  const legacyTenantId = readAdminUserPrimaryTenant(id);
  if (!legacyTenantId) return null;
  return { tenant_id: legacyTenantId, role: 'consulta' };
}

/** Respaldo local de empresa+rol asignados (no reemplaza al API cuando trae `tenants[]`). */
export function persistAdminUserAssignmentSnapshot(
  userId: string,
  snapshot: AdminUserAssignmentSnapshot,
): void {
  const uid = (userId || '').trim();
  const tenant_id = normalizeVaultTenantId(snapshot.tenant_id) || (snapshot.tenant_id || '').trim();
  const role = sanitizeVaultOperativeRole(snapshot.role) || (snapshot.role || '').trim() || 'consulta';
  if (!uid || !tenant_id || typeof localStorage === 'undefined') return;
  const map = readAdminUserAssignmentSnapshotMap();
  map[uid] = {
    tenant_id,
    role,
    ...(snapshot.tenant_name?.trim() ? { tenant_name: snapshot.tenant_name.trim() } : {}),
  };
  writeAdminUserAssignmentSnapshotMap(map);
  persistAdminUserPrimaryTenant(uid, tenant_id);
}

/** Aplica empresa+rol del snapshot local al usuario logueado (Inicio, menú, permisos). */
export function applyAdminAssignmentSnapshotToCurrentUserSession(): boolean {
  return refreshCurrentUserOperativeRoleFromAdminSnapshot();
}

/** Refresca rol y empresa Vault en sesión desde snapshot local (Gestión de Usuarios). */
export function refreshCurrentUserOperativeRoleFromAdminSnapshot(): boolean {
  if (sessionJwtIndicatesVaultSuperadmin()) return false;

  const userId = vaultCurrentUserIdFromSession().trim();
  if (!userId) return false;

  const snapshot = readAdminUserAssignmentSnapshot(userId);
  const role = (snapshot?.role || '').trim();
  const tenantId = normalizeVaultTenantId(snapshot?.tenant_id ?? '');
  if (!role && !tenantId) return false;

  applyVaultOperativeRoleToSession(role || readVaultOperativeRole()?.trim() || 'consulta', {
    tenantId: tenantId || undefined,
    tenantName: snapshot?.tenant_name,
    pin: true,
  });

  if (tenantId) {
    persistVaultTenantScopeToStorage(tenantId);
    persistVaultUserSwitchedTenant(false);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('tenant_id', tenantId);
    }
  }

  return true;
}

/** Sincroniza respaldo local desde el listado API sin borrar empresa/rol ya confirmados en UI. */
export function syncAdminUserAssignmentSnapshotsFromUsers(users: AdminSystemUser[]): void {
  if (typeof localStorage === 'undefined') return;
  const map = readAdminUserAssignmentSnapshotMap();
  for (const user of users) {
    if (user.is_superadmin) continue;
    const prev = map[user.id];
    if (prev?.tenant_id?.trim() && prev?.role?.trim()) {
      const primary = resolvePrimaryAdminUserTenant(user.tenants ?? [], undefined, user.id);
      const apiName = (primary?.tenant_name || '').trim();
      if (apiName && !prev.tenant_name?.trim()) {
        map[user.id] = { ...prev, tenant_name: apiName };
      }
      continue;
    }

    const primary = resolvePrimaryAdminUserTenant(user.tenants ?? [], undefined, user.id);
    const tenantId = (primary?.tenant_id || '').trim();
    const role =
      sanitizeVaultOperativeRole(primary?.role) || (primary?.role || '').trim() || '';

    if (!tenantId && !role) {
      continue;
    }

    const mergedTenantId = tenantId || prev?.tenant_id?.trim() || '';
    if (!mergedTenantId) continue;

    const mergedRole = resolveAdminUserSnapshotRoleMerge(prev, role, mergedTenantId);
    const mergedName =
      (primary?.tenant_name || '').trim() || prev?.tenant_name?.trim() || undefined;

    map[user.id] = {
      tenant_id: mergedTenantId,
      role: mergedRole,
      ...(mergedName ? { tenant_name: mergedName } : {}),
    };
  }
  writeAdminUserAssignmentSnapshotMap(map);
}

function resolvePrimaryFromAdminOverride(
  list: AdminUserTenantInfo[],
  userId: string | undefined,
  catalog?: Array<{ id: string; name: string }>,
): AdminUserTenantInfo | null {
  const overrideId =
    normalizeVaultTenantId(readAdminUserPrimaryTenant(userId ?? '')) ||
    (() => {
      const uid = (userId ?? '').trim();
      const currentUserId = vaultCurrentUserIdFromSession().trim();
      if (!uid || !currentUserId || uid !== currentUserId) return '';
      return normalizeVaultTenantId(resolveVaultActiveTenantId());
    })();
  if (!overrideId) return null;

  const fromList = list.find(
    (row) =>
      normalizeVaultTenantId(row.tenant_id) === overrideId ||
      row.tenant_id.trim() === overrideId,
  );
  if (fromList) return fromList;

  const company = catalog?.find((c) => c.id.trim() === overrideId);
  const snapshot = readAdminUserAssignmentSnapshot(userId ?? '');
  const role =
    list.find((row) => row.role?.trim())?.role ??
    snapshot?.role ??
    'consulta';
  return {
    id: list[0]?.id ?? '',
    tenant_id: overrideId,
    tenant_name: company?.name ?? snapshot?.tenant_name ?? '',
    role,
  };
}

/** Una sola empresa Vault activa por usuario (descarta asignaciones legacy duplicadas). */
export function resolvePrimaryAdminUserTenant(
  rows: AdminUserTenantInfo[],
  catalog?: Array<{ id: string; name: string }>,
  userId?: string,
): AdminUserTenantInfo | null {
  const list = dedupeAdminUserTenants(rows).filter(
    (row) => (row.tenant_id || '').trim() || (row.role || '').trim(),
  );

  const fromOverride = resolvePrimaryFromAdminOverride(list, userId, catalog);
  if (fromOverride) return fromOverride;

  if (!list.length) return null;

  if (list.length === 1) return list[0];

  const catalogIds = new Set(
    (catalog ?? []).map((entry) => entry.id.trim()).filter(Boolean),
  );

  let candidates = list;
  if (catalogIds.size > 0) {
    const inCatalog = list.filter((row) => catalogIds.has(row.tenant_id.trim()));
    if (inCatalog.length === 1) return inCatalog[0];
    if (inCatalog.length > 1) candidates = inCatalog;
  }

  const scored = candidates.map((row, index) => {
    let score = index;
    if (normalizeVaultTenantId(row.tenant_id)) score += 100;
    if (catalogIds.has(row.tenant_id.trim())) score += 200;
    if ((row.id || '').trim()) score += 50;
    const name = (row.tenant_name || '').trim();
    if (name && !isSimappeProductCompanyLabel(name)) score += 25;
    return { row, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.row ?? candidates[candidates.length - 1];
}

export function coalesceAdminUserSingleTenant(
  user: AdminSystemUser,
  catalog?: Array<{ id: string; name: string }>,
): AdminSystemUser {
  if (user.is_superadmin) return user;
  const primary = resolvePrimaryAdminUserTenant(user.tenants ?? [], catalog, user.id);
  if (!primary) return user;
  return { ...user, tenants: [primary] };
}

/** Completa empresa/rol fusionando API + respaldo local (no pierde asignación al recargar). */
export function hydrateAdminUserTenantsFromPersistedPrimary(
  user: AdminSystemUser,
  catalog?: Array<{ id: string; name: string }>,
): AdminUserTenantInfo[] {
  if (user.is_superadmin) return dedupeAdminUserTenants(user.tenants ?? []);

  const existing = dedupeAdminUserTenants(user.tenants ?? []);
  const snapshot = readAdminUserAssignmentSnapshot(user.id);
  const primary = existing.length
    ? resolvePrimaryAdminUserTenant(existing, catalog, user.id)
    : null;

  const tenantId =
    (primary?.tenant_id || '').trim() ||
    (snapshot?.tenant_id || '').trim() ||
    normalizeVaultTenantId(readAdminUserPrimaryTenant(user.id)) ||
    readAdminUserPrimaryTenant(user.id).trim();

  const role =
    sanitizeVaultOperativeRole(snapshot?.role) ||
    (snapshot?.role || '').trim() ||
    sanitizeVaultOperativeRole(primary?.role) ||
    (primary?.role || '').trim() ||
    sanitizeVaultOperativeRole(existing.find((t) => (t.role || '').trim())?.role) ||
    '';

  if (!tenantId && !role) {
    return existing;
  }

  const company = catalog?.find((c) => c.id.trim() === tenantId);
  const tenant_name =
    (primary?.tenant_name || '').trim() ||
    (snapshot?.tenant_name || '').trim() ||
    (company?.name || '').trim() ||
    (existing.find((t) => (t.tenant_name || '').trim())?.tenant_name || '').trim();

  return [
    {
      id: primary?.id ?? existing[0]?.id ?? '',
      tenant_id: tenantId,
      tenant_name,
      role: role || 'consulta',
    },
  ];
}

/**
 * Fuente única para tabla/formulario: fusiona `tenants[]` en memoria + snapshot local.
 */
export function resolveAdminUserTenantAssignmentForDisplay(
  user: AdminSystemUser,
  catalog?: Array<{ id: string; name: string }>,
): AdminUserTenantInfo | null {
  if (user.is_superadmin) return null;

  const fromList = resolvePrimaryAdminUserTenant(user.tenants ?? [], catalog, user.id);
  const hydrated = hydrateAdminUserTenantsFromPersistedPrimary(user, catalog);
  const fromHydrated =
    resolvePrimaryAdminUserTenant(hydrated, catalog, user.id) ?? hydrated[0] ?? null;
  const snapshot = readAdminUserAssignmentSnapshot(user.id);

  const tenant_id =
    (fromList?.tenant_id || '').trim() ||
    (fromHydrated?.tenant_id || '').trim() ||
    (snapshot?.tenant_id || '').trim();
  const role =
    sanitizeVaultOperativeRole(fromList?.role) ||
    (fromList?.role || '').trim() ||
    sanitizeVaultOperativeRole(fromHydrated?.role) ||
    (fromHydrated?.role || '').trim() ||
    sanitizeVaultOperativeRole(snapshot?.role) ||
    (snapshot?.role || '').trim() ||
    '';

  if (!tenant_id && !role) return null;

  const company = catalog?.find((c) => c.id.trim() === tenant_id);
  return {
    id: fromList?.id || fromHydrated?.id || '',
    tenant_id,
    tenant_name:
      (fromList?.tenant_name || '').trim() ||
      (fromHydrated?.tenant_name || '').trim() ||
      (company?.name || '').trim(),
    role: role || 'consulta',
  };
}

export function normalizeAdminSystemUser(raw: unknown): AdminSystemUser {
  const o = asRecord(raw) ?? {};
  const tenants = normalizeAdminUserTenantsFromRecord(o);
  const accessUsername = String(
    o['access_username'] ?? o['accessUsername'] ?? o['username'] ?? '',
  ).trim();
  const simappeUserId = String(
    o['simappe_user_id'] ?? o['simappeUserId'] ?? '',
  ).trim();

  return {
    id: String(o['id'] ?? ''),
    email: String(o['email'] ?? ''),
    full_name: String(o['full_name'] ?? o['fullName'] ?? o['name'] ?? ''),
    is_active: o['is_active'] !== false && o['isActive'] !== false,
    is_superadmin: Boolean(o['is_superadmin'] ?? o['isSuperadmin']),
    access_username: accessUsername || null,
    simappe_user_id: simappeUserId || null,
    created_at: o['created_at'] != null ? String(o['created_at']) : undefined,
    updated_at: o['updated_at'] != null ? String(o['updated_at']) : undefined,
    last_login:
      o['last_login'] != null
        ? String(o['last_login'])
        : o['lastLogin'] != null
          ? String(o['lastLogin'])
          : null,
    tenants,
  };
}

export function userTenantAssignmentsToAdminTenants(
  assignments: UserTenantAssignment[],
): AdminUserTenantInfo[] {
  return dedupeAdminUserTenants(
    assignments.map((a) => ({
      id: a.id,
      tenant_id: a.tenant_id,
      role: a.role,
      tenant_name: a.tenant_name ?? '',
      tenant_slug: a.tenant_slug,
    })),
  );
}

export function userNeedsTenantEnrichment(user: AdminSystemUser): boolean {
  if (user.is_superadmin) return false;
  return !(user.tenants ?? []).some((t) => (t.tenant_id || '').trim());
}

export function mergeAdminUserTenants(
  user: AdminSystemUser,
  tenants: AdminUserTenantInfo[],
): AdminSystemUser {
  if (!tenants.length) return user;
  return { ...user, tenants: dedupeAdminUserTenants(tenants) };
}

export interface ModulePermissionTenantHint {
  user_id: string;
  tenant_id: string;
  tenant_name?: string | null;
}

/** Completa `tenant_id` desde permisos de módulo cuando el listado de usuarios no lo trae. */
export function enrichAdminUsersFromModulePermissions(
  users: AdminSystemUser[],
  permissions: ModulePermissionTenantHint[],
): AdminSystemUser[] {
  const byUser = new Map<string, { tenant_id: string; tenant_name: string }>();
  for (const row of permissions) {
    const userId = (row.user_id || '').trim();
    const tenantId = (row.tenant_id || '').trim();
    if (!userId || !tenantId || byUser.has(userId)) continue;
    byUser.set(userId, {
      tenant_id: tenantId,
      tenant_name: (row.tenant_name || '').trim(),
    });
  }

  return users.map((user) => {
    if (user.is_superadmin) return user;
    if ((user.tenants ?? []).some((t) => (t.tenant_id || '').trim())) return user;
    const hint = byUser.get(user.id);
    if (!hint) return user;
    const role = sanitizeVaultOperativeRole(
      (user.tenants ?? []).find((t) => (t.role || '').trim())?.role,
    ) || 'consulta';
    return mergeAdminUserTenants(user, [
      {
        id: '',
        tenant_id: hint.tenant_id,
        tenant_name: hint.tenant_name,
        role,
      },
    ]);
  });
}

export interface AdminUserTenantCatalogOptions {
  scopeTenantId?: string;
  /** Solo admin de empresa con listado acotado a su tenant; nunca superadmin global. */
  allowScopeInference?: boolean;
}

/** Completa nombre de empresa desde catálogo Vault (`/api/admin/tenants`). */
export function enrichAdminUserTenantsFromCatalog(
  user: AdminSystemUser,
  catalog: Array<{ id: string; name: string }>,
): AdminUserTenantInfo[] {
  return dedupeAdminUserTenants(user.tenants ?? []).map((row) => {
    const tenant_id = (row.tenant_id || '').trim();
    if (!tenant_id) return row;
    const company = catalog.find((c) => c.id === tenant_id);
    const tenant_name = (row.tenant_name || company?.name || '').trim();
    return tenant_name ? { ...row, tenant_name } : row;
  });
}

function resolveAdminUserTenantCatalogOptions(
  options?: AdminUserTenantCatalogOptions | string,
): AdminUserTenantCatalogOptions {
  if (typeof options === 'string') {
    return { scopeTenantId: options, allowScopeInference: false };
  }
  return options ?? {};
}

/** Completa empresa cuando el listado trae rol pero no `tenant_id` (API sin user-tenants). */
export function inferAdminUserTenantsFromCatalog(
  user: AdminSystemUser,
  catalog: Array<{ id: string; name: string }>,
  options?: AdminUserTenantCatalogOptions | string,
): AdminUserTenantInfo[] {
  const opts = resolveAdminUserTenantCatalogOptions(options);
  const enriched = enrichAdminUserTenantsFromCatalog(user, catalog);
  if (enriched.some((t) => t.tenant_id.trim())) return enriched;

  if (!opts.allowScopeInference) return enriched;

  const role = sanitizeVaultOperativeRole(
    enriched.find((t) => t.role?.trim())?.role,
  ) || 'consulta';
  const scopeId = (opts.scopeTenantId ?? '').trim();

  if (scopeId) {
    const company = catalog.find((c) => c.id === scopeId);
    return [
      {
        id: enriched[0]?.id ?? '',
        tenant_id: scopeId,
        role: role || 'consulta',
        tenant_name: company?.name ?? enriched[0]?.tenant_name ?? '',
      },
    ];
  }

  if (catalog.length === 1) {
    const company = catalog[0];
    return [
      {
        id: enriched[0]?.id ?? '',
        tenant_id: company.id,
        role: role || 'consulta',
        tenant_name: company.name,
      },
    ];
  }

  return enriched;
}
